import {
  Box,
  CircularProgress,
  IconButton,
  ThemeProvider,
  Tooltip
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { jaJP } from "@mui/x-data-grid/locales";
import { ReactComponent as ToolEdit } from "assets/icons/estate_info/tool_edit.svg";
import { ReactComponent as ToolTrash } from "assets/icons/estate_info/tool_trash.svg";
import { SafeExternalLink } from "components/Parts/Atoms/SafeExternalLink";
import { StickyNoteInputMode } from "components/Parts/Organisms/StickyNoteInputDialog/enums";
import { StickyNote } from "graphql/API";
import React from "react";
import { StickyNoteItemIcon } from "../StickyNoteItemIcon";
import styles from "./StickyNoteList.module.scss";
import { useStickyNoteListHooks } from "./hooks";
import { StickyNotesListProps } from "./interfaces";

export const StickyNotesList = React.memo((props: StickyNotesListProps) => {
  const { toolTipTheme, dataGridTheme, progressTheme } =
    useStickyNoteListHooks();

  const columns: GridColDef<StickyNote>[] = [
    {
      field: "id",
      headerName: "種別",
      width: 150,
      renderCell: (params) => (
        <Box className={styles.idWrapper}>
          <StickyNoteItemIcon stickyNote={params.row} />
          <Box className={styles.id}> </Box>
          <ThemeProvider theme={toolTipTheme}>
            <Tooltip title="編集" placement="top" followCursor>
              <Box component="span">
                <IconButton
                  className={styles.operationButton}
                  disabled={props.disabled}
                  onClick={() => {
                    props.onStickyNoteClick(
                      params.row as StickyNote,
                      StickyNoteInputMode.Update
                    );
                  }}
                >
                  <Box
                    className={
                      props.disabled ? styles.disabled : styles.editable
                    }
                  >
                    <ToolEdit />
                  </Box>
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title="削除" placement="top" followCursor>
              <Box component="span">
                <IconButton
                  className={styles.operationButton}
                  disabled={props.disabled}
                  onClick={() => {
                    props.onStickyNoteClick(
                      params.row as StickyNote,
                      StickyNoteInputMode.Delete
                    );
                  }}
                >
                  <Box
                    className={
                      props.disabled ? styles.disabled : styles.deletable
                    }
                  >
                    <ToolTrash />
                  </Box>
                </IconButton>
              </Box>
            </Tooltip>
          </ThemeProvider>
        </Box>
      )
    },
    {
      field: "openLevel",
      headerName: "公開範囲",
      width: 150
    },
    {
      field: "branchName",
      headerName: "支店名",
      width: 150
    },
    {
      field: "owner",
      headerName: "作成・更新者ID",
      width: 150
    },
    {
      field: "ownerName",
      headerName: "作成・更新者名",
      width: 180
    },
    {
      field: "updatedAt",
      headerName: "最終更新日",
      width: 200,
      renderCell: (params) =>
        params.value
          ? new Date(params.value).toLocaleDateString("ja-JP", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit"
            })
          : ""
    },
    {
      field: "description",
      headerName: "内容",
      width: 800
    },
    {
      field: "url1",
      headerName: "URL（1）",
      width: 250,
      renderCell: (params) => (
        <SafeExternalLink url={params.value}>{params.value}</SafeExternalLink>
      )
    },
    {
      field: "url2",
      headerName: "URL（2）",
      width: 250,
      renderCell: (params) => (
        <SafeExternalLink url={params.value}>{params.value}</SafeExternalLink>
      )
    },
    {
      field: "url3",
      headerName: "URL（3）",
      width: 250,
      renderCell: (params) => (
        <SafeExternalLink url={params.value}>{params.value}</SafeExternalLink>
      )
    }
  ];

  return (
    <Box id="stickyNote-list" className={styles.stickyNotesList}>
      {props.busy ? (
        <Box className={styles.progress}>
          <ThemeProvider theme={progressTheme}>
            <CircularProgress color={"primary"} />
          </ThemeProvider>
        </Box>
      ) : (
        <>
          <ThemeProvider theme={dataGridTheme}>
            <DataGrid
              rows={props.stickyNotes}
              columns={columns}
              localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
              density="compact"
              className={styles.dataGrid}
            />
          </ThemeProvider>
        </>
      )}
    </Box>
  );
});
